/**
 * Lógica pura de llamados de capital, aportes y cuentas. Montos como texto (ver lib/gastos.ts: parseMonto,
 * montoAInput, parseTipoCambio). El reparto lo calcula el backend (vista previa): acá no se divide nada.
 */
import type { BadgeTono, OpcionSelect } from "@/components";
import type { Aporte, EstadoLlamado, GastosACubrir, Llamado, Moneda } from "./api";
import { formatFecha, formatMoneda, hoyISO } from "./format";
import { TODOS, montoAInput, parseMonto, parseTipoCambio, tipoCambioAInput } from "./gastos";

export { TODOS };

export const ETIQUETA_ESTADO_LLAMADO: Record<EstadoLlamado, string> = { abierto: "Abierta", cubierto: "Cubierta", vencido: "Vencida" };
export const TONO_ESTADO_LLAMADO: Record<EstadoLlamado, BadgeTono> = { abierto: "neutro", cubierto: "exito", vencido: "peligro" };

/** "2026-12-20" + 30 → "2027-01-19" (en UTC, sin zona horaria de por medio). */
export function sumarDias(iso: string, dias: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + dias);
  return d.toISOString().slice(0, 10);
}

// ------------------------------------------------------------------ llamados

export interface FormLlamado {
  fecha: string;
  vencimiento: string;
  monto_usd: string;
  descripcion: string;
  cubierto: string; // "Cubrir con ventas" (USD), tal cual lo escribe el admin
  cubiertoElegido: boolean; // false = usar la sugerencia del backend (min(parte A, disponible de ventas))
}

export type ErroresLlamado = Partial<Record<keyof FormLlamado, string>>;

export interface PayloadLlamado {
  fecha: string;
  vencimiento: string;
  monto_usd: string;
  descripcion: string | null;
  cubierto_con_ventas_usd: string;
}

export function formLlamado(l?: Llamado, hoy = hoyISO()): FormLlamado {
  if (!l) return { fecha: hoy, vencimiento: sumarDias(hoy, 30), monto_usd: "", descripcion: "", cubierto: "", cubiertoElegido: false };
  return {
    fecha: l.fecha,
    vencimiento: l.vencimiento,
    monto_usd: montoAInput(l.monto_usd),
    descripcion: l.descripcion ?? "",
    cubierto: montoAInput(l.cubierto_con_ventas_usd),
    cubiertoElegido: true, // al editar se conserva lo que ya cubría
  };
}

/** "" o "0" → "0"; "300,5" → "300.5"; null si no es un monto válido. */
function parseCubierto(texto: string): string | null {
  const t = texto.trim();
  if (!t || /^0+([.,]0{1,2})?$/.test(t)) return "0";
  return parseMonto(t);
}

export function validarLlamado(f: FormLlamado): ErroresLlamado {
  const e: ErroresLlamado = {};
  if (!f.fecha) e.fecha = "Ingresá la fecha";
  if (!f.vencimiento) e.vencimiento = "Ingresá el vencimiento";
  else if (f.fecha && f.vencimiento < f.fecha) e.vencimiento = "No puede ser anterior a la fecha";
  if (parseMonto(f.monto_usd) === null) e.monto_usd = "Ingresá un monto mayor a cero";
  if (f.descripcion.trim().length > 200) e.descripcion = "Hasta 200 caracteres";
  if (f.cubiertoElegido && parseCubierto(f.cubierto) === null) e.cubierto = "Ingresá un monto (0 para no usar plata de ventas)";
  return e;
}

/** `sugerido`: lo que propuso la vista previa; se usa si el admin no tocó "Cubrir con ventas". */
export function aPayloadLlamado(f: FormLlamado, sugerido = "0"): PayloadLlamado {
  return {
    fecha: f.fecha,
    vencimiento: f.vencimiento,
    monto_usd: parseMonto(f.monto_usd) ?? "0",
    descripcion: f.descripcion.trim() || null,
    cubierto_con_ventas_usd: f.cubiertoElegido ? (parseCubierto(f.cubierto) ?? "0") : sugerido,
  };
}

/** Query de GET /llamados/vista-previa, o null si todavía no hay fecha y monto válidos. Sin cubierto elegido, el
 * backend sugiere; al editar, `llamadoId` hace que el disponible de ventas no descuente lo de ese mismo llamado. */
export function queryVistaPrevia(f: FormLlamado, llamadoId?: number): string | null {
  const monto = parseMonto(f.monto_usd);
  if (!f.fecha || monto === null) return null;
  const cubierto = f.cubiertoElegido ? parseCubierto(f.cubierto) : null;
  return `?fecha=${f.fecha}&monto_usd=${monto}${cubierto !== null ? `&cubierto_con_ventas_usd=${cubierto}` : ""}${llamadoId !== undefined ? `&llamado_id=${llamadoId}` : ""}`;
}

// ------------------------------------------------------------------ propuesta del monto

/** Query de GET /llamados/gastos-a-cubrir, o null sin fecha. */
export function queryGastosACubrir(hasta: string): string | null {
  return hasta ? `?hasta=${hasta}` : null;
}

/** "USD 3.000,00 a pagar hasta el 31/10/2026 (2 gastos)", o que no hay nada. */
export function textoPropuesta(g: GastosACubrir): string {
  const hasta = formatFecha(g.hasta);
  if (g.items.length === 0) return `No hay gastos a pagar hasta el ${hasta}.`;
  const cantidad = g.items.length === 1 ? "1 gasto" : `${g.items.length} gastos`;
  return `${formatMoneda(g.total_usd, "USD")} a pagar hasta el ${hasta} (${cantidad}).`;
}

/** El total como texto del campo Monto ("3000,5"), o null si no hay nada que proponer. */
export function montoPropuesto(g: GastosACubrir | null): string | null {
  if (!g || parseMonto(montoAInput(g.total_usd)) === null) return null;
  return montoAInput(g.total_usd);
}

/** Llega una propuesta nueva: reemplaza el monto si está vacío o si sigue siendo la propuesta anterior (el admin no lo
 * corrigió); si lo corrigió, se respeta. */
export function montoConPropuesta(actual: string, propuestaAnterior: string | null, nueva: string | null): string {
  if (nueva === null) return actual === propuestaAnterior ? "" : actual;
  return actual.trim() === "" || actual === propuestaAnterior ? nueva : actual;
}

// ------------------------------------------------------------------ aportes

export interface FormAporte {
  fiduciante_id: string; // id como texto (Select); "" = sin elegir
  fecha: string;
  monto: string;
  moneda: Moneda;
  tipo_cambio: string;
  nota: string;
  /** id de la caja como texto; "" = la principal (no se manda). */
  caja_id: string;
}

export type ErroresAporte = Partial<Record<"fiduciante_id" | "fecha" | "monto" | "tipo_cambio" | "nota", string>>;

export interface PayloadAporte {
  fiduciante_id: number;
  fecha: string;
  monto: string;
  moneda: Moneda;
  tipo_cambio: string | null;
  nota: string | null;
  caja_id?: number;
}

export function formAporte(a?: Aporte, hoy = hoyISO(), tcSugerido?: string | null, cajaDefecto = ""): FormAporte {
  if (!a) return { fiduciante_id: "", fecha: hoy, monto: "", moneda: "ARS", tipo_cambio: tipoCambioAInput(tcSugerido), nota: "", caja_id: cajaDefecto };
  return {
    fiduciante_id: String(a.fiduciante_id), fecha: a.fecha, monto: montoAInput(a.monto), moneda: a.moneda, tipo_cambio: tipoCambioAInput(a.tipo_cambio),
    nota: a.nota ?? "", caja_id: a.caja_id === null ? "" : String(a.caja_id),
  };
}

export function validarAporte(f: FormAporte): ErroresAporte {
  const e: ErroresAporte = {};
  if (!f.fiduciante_id) e.fiduciante_id = "Elegí el fiduciante";
  if (!f.fecha) e.fecha = "Ingresá la fecha";
  if (parseMonto(f.monto) === null) e.monto = "Ingresá un monto mayor a cero";
  if (f.moneda === "ARS" && parseTipoCambio(f.tipo_cambio) === null) e.tipo_cambio = "Ingresá el tipo de cambio";
  if (f.nota.trim().length > 200) e.nota = "Hasta 200 caracteres";
  return e;
}

export function aPayloadAporte(f: FormAporte): PayloadAporte {
  return {
    fiduciante_id: Number(f.fiduciante_id),
    fecha: f.fecha,
    monto: parseMonto(f.monto) ?? "0",
    moneda: f.moneda,
    tipo_cambio: f.moneda === "ARS" ? parseTipoCambio(f.tipo_cambio) : null,
    nota: f.nota.trim() || null,
    ...(f.caja_id ? { caja_id: Number(f.caja_id) } : {}),
  };
}

export function opcionesFiduciante(lista: { id: number; nombre: string }[]): OpcionSelect[] {
  return lista.map((f) => ({ valor: String(f.id), etiqueta: f.nombre }));
}

export interface FiltrosAportes {
  fiducianteId: string; // TODOS o id
  desde: string;
  hasta: string;
}

export const filtrosAportesIniciales = (): FiltrosAportes => ({ fiducianteId: TODOS, desde: "", hasta: "" });

export function queryAportes(f: FiltrosAportes): string {
  const partes: string[] = [];
  if (f.fiducianteId !== TODOS) partes.push(`fiduciante_id=${f.fiducianteId}`);
  if (f.desde) partes.push(`desde=${f.desde}`);
  if (f.hasta) partes.push(`hasta=${f.hasta}`);
  return partes.length ? `?${partes.join("&")}` : "";
}

// ------------------------------------------------------------------ cuentas

/** Lo que ve un fiduciante cuyo usuario no está vinculado a una ficha (/cuentas/mia → 404). */
export const MENSAJE_SIN_FICHA = "Tu usuario todavía no está vinculado a tu ficha de fiduciante. Avisale a un administrador.";
